import {useMemo} from 'react';

import {
  RegressionStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  getIconAndTextForChartViewStep,
  getIconAndTextForRegressionStep,
} from 'analysis-tool/charting/AnalysisChartingUtils';
import getOutputStepDataForDataFrame from 'analysis-tool/data/getOutputStepDataForDataFrame';
import getStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {getDatasetSectionSidebarItemDatas, SidebarItemData} from 'analysis-tool/ui/sidebar/SidebarItemDatas';
import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getParentSteps, getTopLevelTableStartStepForViewLevelStep} from 'pipelines/StepLineageUtils';

import {isSendToEntryEnabled} from './OutputsUtil';
import {AnalysisOutputOptionItem, AnalysisOutputType, OUTPUT_NAME_SEPARATOR} from './Types';

function flattenSidebarItemDatas(
  sidebarItems: Array<SidebarItemData>,
  pipeline: PipelineGraphQL,
  indentLevel: number,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  return sidebarItems.flatMap((sidebarItem) =>
    [
      ...sidebarItemToAnalysisOutputOptions(sidebarItem, pipeline, indentLevel, shouldEnableOutputFromStep),
    ].concat(
      flattenSidebarItemDatas(
        sidebarItem.childSidebarItems ?? [],
        pipeline,
        indentLevel + 1,
        shouldEnableOutputFromStep
      )
    )
  );
}

function sidebarItemToAnalysisOutputOptions(
  item: SidebarItemData,
  pipeline: PipelineGraphQL,
  indentLevel: number,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  const stepId = (item.mainViewItem as {stepId: string}).stepId;
  const step = getStepFromAnalysis(pipeline, stepId);
  const baseOptionWithNoOutputConfig: AnalysisOutputOptionItem = {
    indentLevel,
    sidebarItemData: item,
    selectorIndex: -1,
    outputConfigs: [],
    disabledReason: null,
  };

  if (
    [
      AnalysisMainViewItemType.FAILED,
      AnalysisMainViewItemType.RUNNING,
      AnalysisMainViewItemType.VISUALIZATION_CONFIGURATION_FOR_DATASET_VIEW,
    ].includes(item.mainViewItem.type)
  ) {
    return [baseOptionWithNoOutputConfig];
  }

  const disabledReason = shouldEnableOutputFromStep != null ? shouldEnableOutputFromStep(step) : null;
  if (disabledReason != null) {
    return [{...baseOptionWithNoOutputConfig, disabledReason}];
  }

  switch (item.mainViewItem.type) {
    case AnalysisMainViewItemType.CHART: {
      if (step.statusCode !== PipelineStepStatusCode.SUCCEEDED) {
        return [baseOptionWithNoOutputConfig];
      }
      const chartStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME
      );
      const chart = chartStepData?.charts[0];

      const dataFrameStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
      );
      const dataFrame = dataFrameStepData?.dataFrames[0];

      assert(dataFrame != null, 'Data frame should not be null');

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
        step,
      };

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            ...(chart != null && !isInaccessible(chart)
              ? [
                  {
                    ...baseOutputConfig,
                    type: AnalysisOutputType.CHART as const,
                    chart,
                    icon: getIconAndTextForChartViewStep(step).icon,
                  },
                ]
              : []),
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET,
              dataFrame,
              icon: 'dataset',
            },
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.RESULT,
              dataFrame,
              targetSpecificMappingState: null,
              icon: 'data_frame',
            },
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.DATA_FRAME: {
      const dataFrameId = item.mainViewItem.dataFrameId;
      const outputStepData = getOutputStepDataForDataFrame(step, dataFrameId)!;
      const optionDataFrame = outputStepData.dataFrames.find(
        (outputDataFrame) => outputDataFrame.id === dataFrameId
      );
      if (optionDataFrame == null) {
        return [{...baseOptionWithNoOutputConfig}];
      }

      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET as const,
              dataFrame: optionDataFrame,
              step,
              icon: 'dataset' as const,
            },
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.RESULT as const,
              dataFrame: optionDataFrame,
              step,
              targetSpecificMappingState: null,
              icon: 'data_frame' as const,
            },
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.VISUALIZATION: {
      const optionDataFrame = step.outputStepDatas[0]?.dataFrames[0];
      if (optionDataFrame == null) {
        return [{...baseOptionWithNoOutputConfig}];
      }
      const baseOutputConfig = {
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.DATASET as const,
              dataFrame: optionDataFrame,
              step,
              icon: 'dataset' as const,
            },
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.RESULT,
              dataFrame: optionDataFrame,
              step,
              targetSpecificMappingState: null,
              icon: 'data_frame',
            },
          ],
        },
      ];
    }
    case AnalysisMainViewItemType.REGRESSION: {
      const optionDataFrame = step.inputStepDatas[0].dataFrames[0];
      const modelOutputDataFrame = step.outputStepDatas.find(
        (stepData) => stepData.name === RegressionStepDataName.MODEL_OUTPUT
      )!.dataFrames[0];
      const modelVariablesDataFrame = step.outputStepDatas.find(
        (stepData) => stepData.name === RegressionStepDataName.MODEL_VARIABLES
      )!.dataFrames[0];
      // We need the regression's parent step because we need the parent data frame to be an output data frame
      const [parentStep] = getParentSteps(pipeline, step);

      const baseOutputConfig = {
        dataFrame: optionDataFrame,
        step,
        outputName: String(item.title),
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };
      const modelOutputBaseOutputConfig = {
        dataFrame: modelOutputDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_OUTPUT}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const modelVariableBaseOutputConfig = {
        dataFrame: modelVariablesDataFrame,
        step,
        outputName: `${item.title}${OUTPUT_NAME_SEPARATOR}${RegressionStepDataName.MODEL_VARIABLES}`,
        parentOutputName:
          getTopLevelTableStartStepForViewLevelStep(pipeline, step)?.outputStepDatas[0].dataFrames[0].name ??
          null,
      };

      const chartStepData = step.outputStepDatas.find(
        ({name}) => name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME
      );
      const chart = chartStepData?.charts[0];

      return [
        {
          ...baseOptionWithNoOutputConfig,
          outputConfigs: [
            // TODO(BNCH-105911) Assert chart is not null after it is required for regressions
            ...(chart != null &&
            !isInaccessible(chart) &&
            (isSendToEntryEnabled() || BenchlingConfig.ENABLE_ANALYSIS_TOOL_SEND_TO_WORKSHEET)
              ? [
                  {
                    ...baseOutputConfig,
                    type: AnalysisOutputType.CHART as const,
                    chart,
                    icon: getIconAndTextForRegressionStep(step).icon,
                  },
                ]
              : []),
            {
              ...baseOutputConfig,
              step: parentStep,
              type: AnalysisOutputType.DATASET as const,
              icon: 'dataset' as const,
            },
            {
              ...baseOutputConfig,
              type: AnalysisOutputType.RESULT,
              step: parentStep,
              icon: 'data_frame',
              targetSpecificMappingState: null,
            },
          ],
        },
        modelOutputDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelOutputBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                {
                  ...modelOutputBaseOutputConfig,
                  type: AnalysisOutputType.RESULT,
                  icon: 'data_frame' as const,
                  targetSpecificMappingState: null,
                },
              ],
            }
          : baseOptionWithNoOutputConfig,
        modelVariablesDataFrame != null
          ? {
              ...baseOptionWithNoOutputConfig,
              outputConfigs: [
                {
                  ...modelVariableBaseOutputConfig,
                  type: AnalysisOutputType.DATASET as const,
                  icon: 'dataset' as const,
                },
                {
                  ...modelVariableBaseOutputConfig,
                  type: AnalysisOutputType.RESULT,
                  icon: 'data_frame',
                  targetSpecificMappingState: null,
                },
              ],
            }
          : baseOptionWithNoOutputConfig,
      ];
    }

    default:
      assertAndContinue(false, 'AnalysisOutputSelector received unexpected item type', {
        itemType: item.mainViewItem.type,
      });
      return [baseOptionWithNoOutputConfig];
  }
}

export default function usePossibleOutputOptions(
  pipeline: PipelineGraphQL,
  shouldEnableOutputFromStep: ((step: GetAnalysisPipelineStep) => string | null) | undefined
): Array<AnalysisOutputOptionItem> {
  return useMemo(
    () =>
      flattenSidebarItemDatas(
        getDatasetSectionSidebarItemDatas(pipeline),
        pipeline,
        0,
        shouldEnableOutputFromStep
      ).map((option, index) => ({
        ...option,
        selectorIndex: index,
      })),
    [pipeline, shouldEnableOutputFromStep]
  );
}
